<!--
  Driven by `open` from the shell rather than by its own state, so the bell and the panel cannot
  disagree about whether it is showing.

  One `*ngIf="view$ | async as view"` wraps everything: each `| async` opens its own subscription,
  and this collection is a live Firestore listener, so several of them would mean several listeners
  paying for the same reads.
-->
<!--
  `role="dialog"` with NO `aria-modal`, deliberately.

  This is a 420px drawer down one side: the page behind it stays visible and usable, and a keyboard
  user can Tab straight out of it — which is the right behaviour for something you glance at rather
  than something you must dismiss. `aria-modal="true"` was set here briefly and was a lie in the
  expensive direction: it tells a screen reader the rest of the page does not exist while Tab still
  reaches it, so the two ways of navigating get different answers. Non-modal is the honest claim,
  and it is what `aria-modal` defaults to.

  Trapping focus instead would make the claim true, and was rejected: it would force a nurse to
  close the panel before touching anything else in the app.

  The one case that would change this: below 600px the panel is `width: 100vw` and does cover the
  screen, so there it IS modal in practice. If that layout ever gets its own treatment, it wants
  `cdkTrapFocus` (`@angular/cdk/a11y`, already installed) AND `aria-modal` together — never one
  without the other, which is the mistake this comment exists to prevent repeating.
-->
<aside class="notifications-panel" *ngIf="open" role="dialog" aria-label="Notifications" (keydown.escape)="onEscape()">
  <ng-container *ngIf="view$ | async as view">
    <header class="notifications-panel__header">
      <div class="notifications-panel__title-group">
        <span class="notifications-panel__title">Notifications</span>
        <span class="notifications-panel__unread-label">{{ view.unread ? view.unread + ' unread' : 'All caught up' }}</span>
      </div>

      <div class="notifications-panel__actions">
        <!-- Disabled, never hidden: a control that vanishes when everything is read makes the
             header jump, and the label above already says the state. -->
        <button type="button" class="notifications-panel__mark-all" [disabled]="!view.unread" (click)="onMarkAllRead(view.rows)">
          Mark all read
        </button>

        <button #closeButton type="button" class="notifications-panel__close" (click)="closed.emit()" aria-label="Close notifications">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </header>

    <div class="notifications-panel__body">
      <div class="notifications-panel__group" *ngFor="let group of view.groups; trackBy: trackByLabel">
        <div class="notifications-panel__group-label">{{ group.label }}</div>

        <div
          class="notifications-panel__row"
          *ngFor="let row of group.items; trackBy: trackById"
          [class.notifications-panel__row--unread]="!row.read"
          (click)="onRowClick(row)"
          (keydown)="onRowKeydown($event, row)"
          role="button"
          tabindex="0"
          [attr.aria-label]="(row.read ? 'Read: ' : 'Unread: ') + row.description"
        >
          <!-- `title` from the catalog: "CD" on its own tells a reader nothing, and the row's own
               aria-label already carries the description, so this is hover help rather than a11y. -->
          <div class="notifications-panel__tile" [attr.title]="row.typeLabel">{{ row.initials }}</div>

          <div class="notifications-panel__row-body">
            <span class="notifications-panel__description">{{ row.description }}</span>

            <div class="notifications-panel__meta">
              <span class="notifications-panel__type-chip">{{ row.type }}</span>
              <!-- The patient, when there is one. A discharge with no patient still gets a row. -->
              <span class="notifications-panel__patient" *ngIf="row.patient?.name">{{ row.patient?.name }}</span>
            </div>
          </div>

          <div class="notifications-panel__side">
            <!--
              How long ago, not a clock time: a list that spans weeks needs "21 days" to be read at
              a glance, and "9:41 AM" on a row from last month tells the reader nothing.

              The exact timestamp moves to the tooltip rather than being dropped — it is what
              someone reaches for when a discharge date is in question, and losing it to save a
              column would be a worse trade than showing it on hover. Empty title on a pending
              write, so the browser shows no empty tooltip box.
            -->
            <span class="notifications-panel__time" [title]="row.date ? (row.date | dateTimeCustom) : ''">{{ row.date | timeAgo }}</span>
            <span class="notifications-panel__dot" *ngIf="!row.read"></span>
          </div>
        </div>
      </div>

      <div class="notifications-panel__empty" *ngIf="!view.groups.length">
        <mat-icon>notifications_none</mat-icon>
        <span>Nothing to work right now.</span>
      </div>
    </div>
  </ng-container>
</aside>

<!--
  The design's "VIEW ALL NOTIFICATIONS" footer is deliberately not here: there is no /notifications
  route yet, and a link to a page that does not exist is worse than no link. It comes back with the
  full page.
-->
